import styled from '@emotion/styled'
import {
  Container,
  Box,
  Button,
  Typography,
  Chip,
  Card,
  Paper,
  Grid,
} from '@mui/material'
import { typography } from '@resiliatech/birdsong'
import { colors } from '../../../../Themes/Colors'

export const PageContainer = styled(Container)`
  padding-top: 24px;
  padding-bottom: 24px;
  max-width: 1200px;
`

export const HeaderSection = styled(Box)`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 32px;
`

export const BackButton = styled(Button)`
  min-width: 40px;
  padding: 8px;
  color: ${colors.mediumGrey};

  &:hover {
    background-color: ${colors.lightestGrey};
  }
`

export const TitleSection = styled(Box)`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 8px;
`

export const Title = styled(Typography)`
  ${typography.up5.semiBoldSans}
  color: ${colors.darkGrey};
`

export const Subtitle = styled(Typography)`
  ${typography.up2.regularSans}
  color: ${colors.mediumGrey};
`

const getStatusBackgroundColor = (status: boolean) =>
  status ? colors.completedGreen : colors.lightYellow

const getStatusColor = (status: boolean) =>
  status ? colors.darkGreen : colors.paletteOrange

export const StatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'status',
})<{ status: boolean }>`
  background-color: ${({ status }) => getStatusBackgroundColor(status)};
  color: ${({ status }) => getStatusColor(status)};
  font-weight: 500;

  .MuiChip-icon {
    color: inherit;
  }
`

export const InfoCard = styled(Card)`
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`

export const InfoGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
`

export const InfoItem = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

export const InfoLabel = styled(Typography)`
  ${typography.up1.regularSans}
  color: ${colors.mediumGrey};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

export const InfoValue = styled(Typography)`
  ${typography.up2.regularSans}
  color: ${colors.darkGrey};
`

export const SectionTitle = styled(Typography)`
  ${typography.up3.semiBoldSans}
  color: ${colors.darkGrey};
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    color: ${colors.resiliaBlue};
    font-size: 24px;
  }
`

export const MessageBox = styled(Paper)`
  padding: 20px;
  background-color: ${colors.lightestGrey};
  border-left: 4px solid ${colors.resiliaBlue};
  border-radius: 4px;
  white-space: pre-wrap;
  word-break: break-word;
  ${typography.up2.regularSans}
  color: ${colors.darkGrey};
  line-height: 1.6;
`

export const TriggerDataBox = styled(Paper)`
  padding: 16px;
  background-color: ${colors.lightestGrey};
  border-radius: 4px;
  overflow-x: auto;

  pre {
    margin: 0;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 13px;
    line-height: 1.5;
    color: ${colors.darkGrey};
  }

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${colors.lightGrey};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${colors.mediumGrey};
    border-radius: 4px;

    &:hover {
      background: ${colors.darkGrey};
    }
  }
`

export const ActionBar = styled(Box)`
  position: sticky;
  bottom: 0;
  background-color: ${colors.resiliaWhite};
  border-top: 1px solid ${colors.lightGrey};
  padding: 20px 0;
  margin-top: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  button {
    min-width: 250px;
  }
`

export const LoadingContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`

export const ErrorContainer = styled(Container)`
  padding: 48px;
  text-align: center;
  margin-top: 48px;
`
